import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { motion, useScroll } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import Nav from "@/components/store/Nav";
import Hero from "@/components/store/Hero";
import Ribbon from "@/components/store/Ribbon";
import Manifesto from "@/components/store/Manifesto";
import Benefits from "@/components/store/Benefits";
import Checkout from "@/components/store/Checkout";
import BasketDrawer from "@/components/store/BasketDrawer";
import Footer from "@/components/store/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Store() {
  const [qty, setQty] = useState(0);
  const [basketOpen, setBasketOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { scrollYProgress } = useScroll();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollTo = (id) => {
    if (id === "top") {
      lenisRef.current ? lenisRef.current.scrollTo(0) : window.scrollTo({ top: 0 });
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    lenisRef.current ? lenisRef.current.scrollTo(el, { offset: -64 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  const addBottle = () => {
    setQty((q) => q + 1);
    toast.success("Added to basket");
  };

  const placeOrder = async (form) => {
    if (qty < 1) {
      toast.error("Your basket is empty. Add at least one bottle.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        email: form.email,
        full_name: form.full_name,
        location: form.location,
        quantity: qty,
      });
      toast.success(`Order confirmed — receipt sent to ${form.email}`);
      redirectToPaypal(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const redirectToPaypal = (order) => {
    const amount = (order.total - order.shipping).toFixed(2);
    const f = document.createElement("form");
    f.action = "https://www.paypal.com/cgi-bin/webscr";
    f.method = "post";
    f.target = "_blank";
    const fields = {
      cmd: "_xclick",
      business: "hrally@yahoo.com",
      item_name: `Ubiquinol CoQ10 x${order.quantity} (NPN 80075106)`,
      amount: amount,
      shipping: order.shipping.toFixed(2),
      currency_code: "CAD",
      quantity: "1",
    };
    Object.entries(fields).forEach(([k, v]) => {
      const inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = k;
      inp.value = v;
      f.appendChild(inp);
    });
    document.body.appendChild(f);
    f.submit();
    document.body.removeChild(f);
  };

  return (
    <div className="relative bg-[#052221] min-h-screen">
      <div className="animated-teal-bg" aria-hidden="true" />
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="fixed top-0 left-0 right-0 h-[3px] bg-[#D4AF37] origin-left z-[90]"
      />
      <Nav qty={qty} onBasket={() => setBasketOpen(true)} onNav={scrollTo} />
      <div className="relative z-10">
        <Hero onAdd={addBottle} onBuy={() => scrollTo("checkout")} />
        <Ribbon />
        <Manifesto />
        <Benefits onAdd={addBottle} />
        <Checkout qty={qty} setQty={setQty} onSubmit={placeOrder} submitting={submitting} />
        <Footer onNav={scrollTo} />
      </div>
      <BasketDrawer
        open={basketOpen}
        onClose={() => setBasketOpen(false)}
        qty={qty}
        setQty={setQty}
        onCheckout={() => {
          setBasketOpen(false);
          scrollTo("checkout");
        }}
      />
    </div>
  );
}
